import { Checkbox, Dropdown, Input, Modal, Textarea, Button, CIInfoIcon, accessToken } from 'ccs-ui-components';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import axios from 'axios';
import { Container, Form, FormGroup } from 'react-bootstrap';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { BUSINESS_EVENT, cloneConstant, EVENT_DETAILS_MAX_LENGTH, eventTypes, HELP_CENTER_CHATPAGE_SERVICE_BASE_URL, STATUS_TYPES, STRING_CONSTANTS } from '../../../../constants';
import { EventDetailsFormData, EventDetailsFormRef, IEventsDetails, IEventsDetailsFormProps } from '../../../../models/index';
import useEvent from '../../../../store/_api/events/EventChecker';
import { useSelector } from '../../../../store/AppStore';
import { getEventDetailsHelperText, getEventDetailsValidationError, isEventNameInputDisabled } from '../../../../utilities/events/EventsListUtils';
import classes from './EventDetailsForm.module.scss';

const defaultProps = {
  ref: null
};

const EventDetails = ({
  formData,
  handleInputChange,
  setFormParamValues,
  preview,
  isEditFormClicked,
  checkBoxChange,
  isCheckClicked,
  setSensitiveFieldsArr,
  sensitiveFieldsArr,
  showEventNameExistWarning,
  ref
}: IEventsDetails) => {
  // 3rd party initialization
  const { t: translate } = useTranslation();
  const { mode } = useParams<{ mode: string }>();

  // hook initialization
  const { getEventByNameAndMultipleStatus } = useEvent();
  const route = useLocation();
  const cloneEventName = route.state?.cloneEventName ?? '';
  const inputRef = useRef<HTMLInputElement>(null);

  // states
  const [eventTypeValue, setEventTypeValue] = useState('');
  const [getEventTypes, setGetEventTypes] = useState(false);
  const [apiResponseStatus, setApiResponseStatus] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showConfirmDialogOnEventTypeChange, setShowConfirmDialogOnEventTypeChange] = useState(false);
  const [showEventExistWarning, setShowEventExistWarning] = useState<boolean>(false);

  // get store data
  const { eventDetails, isLoading } = useSelector((state) => state.getEventIdReducer);
  const { success } = useSelector((state) => state.eventReducer);

  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);

  // object array of event object
  const eventTypesData = eventTypes.map((type, value) => ({
    name: type.eventType,
    value
  }));

  const EVENT_VALIDATION_ERRORS = getEventDetailsValidationError(translate);

  const HELPER_TEXT = getEventDetailsHelperText(translate);

  const onHighlightFieldClick = () => {
    inputRef?.current?.focus();
  };

  useImperativeHandle(ref, () => ({
    onHighlightFieldClick
  }));

  /**
   * @method typeChangeHandler
   * @description dropdown change event handler
   * @param e change event
   */
  const typeChangeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const index = parseInt(e.target.value, 10);
    const newFormData = { ...formData };
    if (newFormData.eventType.value === eventTypesData[1]?.name && isCheckClicked && sensitiveFieldsArr.length) {
      setShowConfirmDialogOnEventTypeChange(true);
    } else {
      setShowConfirmDialogOnEventTypeChange(false);
      setEventTypeValue(e.target.value);
      newFormData.eventType.value = eventTypesData[index]?.name;
      setFormParamValues(newFormData);
      checkBoxChange(true);
      setSensitiveFieldsArr([]);
    }
  };

  /**
   * @method eventNameValidation
   * @description Validates the entered event ID exist or not
   */
  const eventNameValidation = () => {
    const eventName = formData.eventName.value;
    if (!formData.eventName.isValid) {
      return;
    }
    if (eventName) {
      setApiResponseStatus(true);
      // check for all status except inactive
      const statusList = [STATUS_TYPES.ACTIVE, STATUS_TYPES.DRAFT, STATUS_TYPES.RETURNED, STATUS_TYPES.PENDING_APPROVAL];
      const existingEvent = getEventByNameAndMultipleStatus(formData.eventName.value, statusList, true);
      existingEvent.then((resp: { status: number; data: { totalRecords: number } | undefined }) => {
        if (resp.status === STRING_CONSTANTS.STATUS_CODE_SUCCESS_DATA) {
          if (resp.data && resp.data?.totalRecords > 0) {
            setShowEventExistWarning(true);
          } else {
            setShowEventExistWarning(false);
          }
        }
        setApiResponseStatus(false);
      }).catch(() => {
        setApiResponseStatus(false);
        const newFormData = { ...formData };
        newFormData.eventName.isValid = false;
        newFormData.eventName.error = EVENT_VALIDATION_ERRORS.eventVerificationIssue;
        newFormData.eventName.isTouched = true;
        setFormParamValues(newFormData);
      });
    } else if (!eventName) {
      const newFormData = { ...formData };
      newFormData.eventName.isValid = false;
      newFormData.eventName.error = EVENT_VALIDATION_ERRORS.eventName;
      newFormData.eventName.isTouched = true;
      setFormParamValues(newFormData);
    }
  };

  /**
   * @method handleCheckBoxChange
   * @description handler for checkbox
   */
  const handleCheckBoxChange = () => {
    if (isCheckClicked && sensitiveFieldsArr.length) {
      setShowConfirmDialog(true);
    } else {
      checkBoxChange(isCheckClicked);
      setSensitiveFieldsArr([]);
      setShowConfirmDialog(false);
    }
  };

  /**
   * @method onUncheckAuthDetails
   * @description Handles the event when the "Auth Details" checkbox is unchecked.
   */
  const onUncheckAuthDetails = () => {
    setShowConfirmDialog(false);
    checkBoxChange(isCheckClicked);
    setSensitiveFieldsArr([]);
  };

  /**
   * @method onEventTypeChange
   * @description Handles the change event of the event type.
   */
  const onEventTypeChange = () => {
    setShowConfirmDialogOnEventTypeChange(false);
    const newFormData = { ...formData };
    setEventTypeValue('0');
    newFormData.eventType.value = eventTypesData[0]?.name;
    setFormParamValues(newFormData);
    checkBoxChange(true);
    setSensitiveFieldsArr([]);
  };

  const handleSuggestDescriptionClick = async () => {
    if (!formData.eventName.value) {
      return;
    }
    setIsGeneratingDescription(true);
    try {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken.value}`,
      };
      const apiUrl = `${HELP_CENTER_CHATPAGE_SERVICE_BASE_URL}/generate-event-description?event_name=${formData.eventName.value}`;
      const response = await axios.get(apiUrl, { headers });
      if (response.data?.description && setFormParamValues) {
        setFormParamValues((previousState) => {
          const data = structuredClone(previousState);
          data.description.value = response.data.description.trim();
          data.description.isValid = true;
          data.description.isTouched = true;
          return data;
        });
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error generating AI suggestion. Please try again.');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // fetch list of event types
  useEffect(() => {
    setGetEventTypes(true);
    const newFormData = { ...formData };
    newFormData.eventName.isValid = true;
    newFormData.eventName.value = eventDetails[0]?.eventName;
    setFormParamValues(newFormData);
  }, [preview, success, isEditFormClicked, mode]);

  useEffect(() => {
    if (!preview && !isEditFormClicked) {
      // default show Business Event while creating new event
      eventTypes?.forEach((data, eventIndex) => {
        if (data.eventType === BUSINESS_EVENT) {
          const newFormData = new EventDetailsFormData();
          setEventTypeValue(`${eventIndex}`);
          newFormData.eventType.value = data.eventType;
          setFormParamValues(newFormData);
        }
      });
    }
  }, [getEventTypes, preview, isEditFormClicked]);

  /**
  * This block will execute on first time when pages becomes visible and identify the navigation type
  * if user navigates from the event list page, set the event list item values
  * else set the default empty values
  */
  useEffect(() => {
    // if user navigates form event list page, set the event list item values
    if (!isLoading && eventDetails && (preview || isEditFormClicked || cloneEventName?.length)) {
      const [activeEvent] = eventDetails;
      if (activeEvent?.eventName && activeEvent?.eventType) {
        const newFormData = { ...formData };
        eventTypes?.forEach((data, eventIndex) => {
          if (activeEvent.eventType === data.eventType) {
            setEventTypeValue(`${eventIndex}`);
            newFormData.eventType.value = data.eventType;
          }
        });
        newFormData.eventType.isValid = true;
        newFormData.eventType.value = activeEvent?.eventType;
        newFormData.eventName.isValid = true;
        newFormData.eventName.value = cloneEventName?.length ? `${cloneConstant.CLONE_TXT}-${cloneEventName}` : activeEvent?.eventName;

        newFormData.description.value = activeEvent?.eventDescription;
        setFormParamValues(newFormData);
      }
    }
    if (preview) {
      const newFormData = new EventDetailsFormData();
      newFormData.eventName.isTouched = false;
      newFormData.eventName.isValid = true;
      newFormData.eventName.error = '';
      setFormParamValues(newFormData);
    }
  }, [eventDetails, isLoading]);

  useEffect(() => {
    showEventNameExistWarning(showEventExistWarning);
  }, [showEventExistWarning]);

  const getCheckBoxInput = () => {
    if (formData.eventType.value.toLocaleLowerCase() === BUSINESS_EVENT.toLocaleLowerCase()) {
      return (
        <Row>
          <Form.Group className="mb-3" controlId="form-encryption-check">
            <Checkbox
              type="checkbox"
              label={translate('event_details_form_component.lbl_encryption_check')}
              onChange={handleCheckBoxChange}
              checked={isCheckClicked}
              data-testid="encryption-check"
            />
          </Form.Group>
        </Row>);
    }
     return null;
  };

  return (
    <div className={`form-wrapper-container ${classes['event-details-container']}`}>
      <Container fluid>
        <Row className={classes['event-details-title-container']}>
          <Col sm={12} md={12} lg={12} className="no-padding">
            <div className={classes['main-title']}>
              {translate('event_details_form_component.title_event_details')}
            </div>
            <div className={classes['sub-title']}>{translate('event_details_form_component.sub_title_event_details')}</div>
          </Col>
        </Row>
        <Row>
          <Col sm={8} md={7} lg={7} className="no-padding">
            <Form className={classes['form-container']} onSubmit={(event) => { event.preventDefault(); }} data-testid="eventDetailForm">
              <fieldset disabled={preview}>
                <FormGroup className="mb-3" controlId="eventName" ref={inputRef}>
                  <Input
                    className={`${showEventExistWarning ? classes['input-warning'] : ''}`}
                    isMandatory
                    labelText={String(translate('event_details_form_component.lbl_event_name'))}
                    disabled={isEventNameInputDisabled(isEditFormClicked, cloneEventName, apiResponseStatus)}
                    placeholder={
                      String(translate('event_details_form_component.lbl_event_name_example'))
                    }
                    onChange={handleInputChange}
                    value={formData.eventName.value}
                    name={formData.eventName.name}
                    maxLength={EVENT_DETAILS_MAX_LENGTH.eventName}
                    error={preview ? false : !formData.eventName.isValid}
                    onBlur={eventNameValidation}
                    data-testid={formData.eventName.name}
                    helperText={HELPER_TEXT.event_name_ht}
                    errorTxt={preview ? '' : formData.eventName.error}
                    isLoading={apiResponseStatus}
                    infoIconTxt={String(translate('event_details_form_component.event_name_info_bubble'))}
                  />
                </FormGroup>

                <FormGroup controlId="eventType">
                  <Dropdown
                    label=""
                    isMandatory
                    labelText={String(translate('event_details_form_component.lbl_event_type'))}
                    data={eventTypesData}
                    className={classes['dropdown-control']}
                    value={eventTypeValue}
                    onChange={typeChangeHandler}
                    name={formData.eventType.name}
                    data-testid={formData.eventType.name}
                    infoIconTxt={String(translate('event_details_form_component.event_type_info_bubble'))}
                    placeholder={String(translate('fsm_registry_form.lbl_placeholder_select'))}
                  />
                </FormGroup>
                {getCheckBoxInput()}
                <FormGroup controlId="eventDescription">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label htmlFor="eventDescription">{String(translate('event_details_form_component.lbl_event_description'))}</label>
                    <div className={classes['jolt-config-beta-link']}>
                      <div className={classes['button-group']}>
                        <Button
                          variant="tertiary"
                          className={classes['configure-jolt-btn']}
                          onClick={handleSuggestDescriptionClick}
                          disabled={!formData.eventName.value || isGeneratingDescription}
                          data-testid="suggest-description-btn"
                        >
                          <small>{isGeneratingDescription ? 'Generating...' : 'Suggest Description'}</small>
                          <CIInfoIcon iconTxt={String(!formData.eventName.value ? 'Enter event name first' : 'Get AI suggestion for description')} />
                        </Button>
                        <span className={classes['btn-beta-text']}>[Beta]</span>
                      </div>
                    </div>
                  </div>
                  <Textarea
                    value={formData.description.value}
                    placeholder={String(translate('event_details_form_component.plchd_event_description'))}
                    className={classes.description}
                    name={formData.description.name}
                    maxLength={EVENT_DETAILS_MAX_LENGTH.eventDescription}
                    onChange={handleInputChange}
                    data-testid="event-description"
                    helperText={HELPER_TEXT.desc_ht}
                    errorTxt={formData.description.error}
                    error={!formData.description.isValid}
                    infoIconTxt={String(translate('event_details_form_component.event_description_info_bubble'))}
                  />
                </FormGroup>
              </fieldset>
            </Form>
          </Col>
          <Col sm={4} md={5} lg={5} className="no-padding">
            <div className={classes['form-subtext']}>
              <ul>
                <li> {translate('event_details_form_component.event_form_subtext1')}</li>
                <li> {translate('event_details_form_component.event_form_subtext2')}</li>
                <li> {translate('event_details_form_component.event_form_subtext3')}</li>
                <li> {translate('event_details_form_component.event_form_subtext4')}</li>
              </ul>
            </div>
          </Col>
        </Row>
      </Container>
      <Modal
        modalTitle={translate('event_details_form_component.remove_encryption_heading')}
        modalShow={showConfirmDialog}
        modalBody={translate('event_details_form_component.remove_encryption_modal_body')}
        btnCloseText={String(translate('comment_dialog.lbl_no'))}
        btnSaveText={String(translate('comment_dialog.lbl_yes'))}
        onCloseModal={() => {
          setShowConfirmDialog(false);
        }}
        onSaveModal={onUncheckAuthDetails}
        alignToCentre
        disableSaveBtn={false}
        disableCloseBtn={false}
        type="warning"
      />
      <Modal
        modalTitle={translate('event_details_form_component.eventtype_change_modal_heading')}
        modalShow={showConfirmDialogOnEventTypeChange}
        modalBody={translate('event_details_form_component.eventtype_change_modal_body')}
        btnCloseText={String(translate('comment_dialog.lbl_no'))}
        btnSaveText={String(translate('comment_dialog.lbl_yes'))}
        onCloseModal={() => {
          setShowConfirmDialogOnEventTypeChange(false);
        }}
        onSaveModal={onEventTypeChange}
        alignToCentre
        disableSaveBtn={false}
        disableCloseBtn={false}
        type="warning"
      />
    </div>
  );
};
EventDetails.defaultProps = defaultProps;

const ServiceDetailsForm = forwardRef<EventDetailsFormRef, IEventsDetailsFormProps>(({ formData,
  handleInputChange,
  setFormParamValues,
  preview,
  isEditFormClicked,
  checkBoxChange,
  isCheckClicked,
  setSensitiveFieldsArr,
  sensitiveFieldsArr,
  showEventNameExistWarning }: IEventsDetailsFormProps, ref) => <EventDetails
    formData={formData}
    handleInputChange={handleInputChange}
    setFormParamValues={setFormParamValues}
    preview={preview}
    isEditFormClicked={isEditFormClicked}
    checkBoxChange={checkBoxChange}
    isCheckClicked={isCheckClicked}
    setSensitiveFieldsArr={setSensitiveFieldsArr}
    sensitiveFieldsArr={sensitiveFieldsArr}
    showEventNameExistWarning={showEventNameExistWarning}
    ref={ref}
  />);
export default ServiceDetailsForm;
